import { ComponentRef, Type } from '@angular/core';
import { TemplateContext } from '../models/reportTemplate.model';
import { EditorPropertySchema } from '../components/template-widgets/sample-widgets/widget-config.helpers';
import { GravimetricWidgetComponent } from '../components/template-widgets/sample-widgets/gravimetric-widget/gravimetric-widget.component';
import { configureGravimetricWidget, gravimetricEditorPropertySchema, gravimetricLoopTemplate, GravimetricWidgetAttr } from '../components/template-widgets/sample-widgets/gravimetric-widget/gravimetric-widget.config';
import { PpeListWidgetComponent } from '../components/template-widgets/sample-widgets/ppe-list-widget/ppe-list-widget.component';
import { configurePpeListWidget, ppeListEditorPropertySchema, ppeListLoopTemplate } from '../components/template-widgets/sample-widgets/ppe-list-widget/ppe-list-widget.config';
import { MultiSampleTableWidgetComponent } from '../components/template-widgets/sample-widgets/multi-sample-table-widget/multi-sample-table-widget.component';
import { configureMultiSampleTableWidget, multiSampleTableEditorPropertySchema, multiSampleTableLoopTemplate, MultiSampleTableWidgetAttr, multiSampleLoopScopes } from '../components/template-widgets/sample-widgets/multi-sample-table-widget/multi-sample-table-widget.config';
import { RiskRegisterTableWidgetComponent } from '../components/template-widgets/risk-widgets/risk-register-table-widget/risk-register-table-widget.component';
import { configureRiskRegisterTableWidget, riskRegisterTableEditorPropertySchema, riskRegisterTableLoopTemplate, riskRegisterTableLoopScopes } from '../components/template-widgets/risk-widgets/risk-register-table-widget/risk-register-table-widget.config';
import { SegProfileHeaderWidgetComponent } from '../components/template-widgets/risk-widgets/seg-profile-header-widget/seg-profile-header-widget.component';
import { configureSegProfileHeaderWidget, segProfileHeaderEditorPropertySchema, segProfileHeaderLoopTemplate, segProfileHeaderLoopScopes, SegProfileHeaderWidgetAttr } from '../components/template-widgets/risk-widgets/seg-profile-header-widget/seg-profile-header-widget.config';
import { SegJobRolesWidgetComponent } from '../components/template-widgets/risk-widgets/seg-job-roles-widget/seg-job-roles-widget.component';
import { configureSegJobRolesWidget, segJobRolesEditorPropertySchema, segJobRolesLoopTemplate, segJobRolesLoopScopes } from '../components/template-widgets/risk-widgets/seg-job-roles-widget/seg-job-roles-widget.config';
import { SegAgentsWidgetComponent } from '../components/template-widgets/risk-widgets/seg-agents-widget/seg-agents-widget.component';
import { configureSegAgentsWidget, segAgentsEditorPropertySchema, segAgentsLoopTemplate, segAgentsLoopScopes, SegAgentsWidgetAttr } from '../components/template-widgets/risk-widgets/seg-agents-widget/seg-agents-widget.config';
import { SegControlsWidgetComponent } from '../components/template-widgets/risk-widgets/seg-controls-widget/seg-controls-widget.component';
import { configureSegControlsWidget, segControlsEditorPropertySchema, segControlsLoopTemplate, segControlsLoopScopes, SegControlsWidgetAttr } from '../components/template-widgets/risk-widgets/seg-controls-widget/seg-controls-widget.config';
import { SegTasksWidgetComponent } from '../components/template-widgets/risk-widgets/seg-tasks-widget/seg-tasks-widget.component';
import { configureSegTasksWidget, segTasksEditorPropertySchema, segTasksLoopTemplate, segTasksLoopScopes, SegTasksWidgetAttr } from '../components/template-widgets/risk-widgets/seg-tasks-widget/seg-tasks-widget.config';
import { CocSampleTableWidgetComponent } from '../components/template-widgets/coc-widgets/coc-sample-table-widget/coc-sample-table-widget.component';
import { configureCocSampleTableWidget, cocSampleTableEditorPropertySchema, cocSampleTableLoopTemplate, cocSampleTableLoopScopes } from '../components/template-widgets/coc-widgets/coc-sample-table-widget/coc-sample-table-widget.config';

export type WidgetConfigureFn = (
    componentRef: ComponentRef<unknown>,
    context: TemplateContext,
    sourceElement?: HTMLElement
) => void;

export interface WidgetLoopScope {
    loopExpr: string;
    label: string;
    fields: string[];
}

export interface WidgetRegistration {
    tagName: string;
    component: Type<any>;
    componentName: string;
    displayLabel: string;
    inputs?: string[];
    description?: string;
    configureInputs: WidgetConfigureFn;
    editorPropertySchema: EditorPropertySchema;
    loopTemplate?: string;
    editorAtomic?: boolean;
    editorSchematicHtml?: string;
    loopScopes?: WidgetLoopScope[];
}

export interface EntityWidgetRegistry {
    [entityType: string]: WidgetRegistration[];
}

export const ENTITY_TYPES = {
    SAMPLE: 'Sample',
    SAMPLE_SURVEY: 'SampleSurvey',
    MULTI_SAMPLE: 'MultiSample',
    RISK_REGISTER: 'RiskRegister',
    EMPLOYEE_LETTER: 'EmployeeLetter',
    CHAIN_OF_CUSTODY: 'ChainOfCustody'
} as const;

export type EntityType = typeof ENTITY_TYPES[keyof typeof ENTITY_TYPES];

export const WIDGET_REGISTRY: EntityWidgetRegistry = {
    [ENTITY_TYPES.SAMPLE]: [
        {
            tagName: 'app-gravimetric-widget',
            component: GravimetricWidgetComponent,
            componentName: 'GravimetricWidgetComponent',
            displayLabel: 'Gravimetric Results Widget',
            inputs: ['data', GravimetricWidgetAttr.ShowHeader, GravimetricWidgetAttr.CardTemplate],
            description: 'Gravimetric results widget showing agent exposure data with progress bars and jurisdictions',
            configureInputs: configureGravimetricWidget,
            editorPropertySchema: gravimetricEditorPropertySchema,
            loopTemplate: gravimetricLoopTemplate
        },
        {
            tagName: 'app-ppe-list-widget',
            component: PpeListWidgetComponent,
            componentName: 'PpeListWidgetComponent',
            displayLabel: 'PPE List Widget',
            inputs: ['ppeAssignments', 'ihLabResults', 'sampleType'],
            description: 'PPE list widget showing personal protective equipment assignments',
            configureInputs: configurePpeListWidget,
            editorPropertySchema: ppeListEditorPropertySchema,
            loopTemplate: ppeListLoopTemplate
        }
    ],
    [ENTITY_TYPES.SAMPLE_SURVEY]: [
        {
            tagName: 'app-multi-sample-table-widget',
            component: MultiSampleTableWidgetComponent,
            componentName: 'MultiSampleTableWidgetComponent',
            displayLabel: 'Multi-Sample Table Widget',
            inputs: ['data', MultiSampleTableWidgetAttr.ShowHeader, MultiSampleTableWidgetAttr.ExcludeAdjustedOel, 'excludedOelColumnKeys'],
            description: 'Multi-sample table widget showing survey data, sample information, and agent concentrations across multiple samples',
            configureInputs: configureMultiSampleTableWidget,
            editorPropertySchema: multiSampleTableEditorPropertySchema,
            loopTemplate: multiSampleTableLoopTemplate,
            loopScopes: multiSampleLoopScopes
        }
    ],
    [ENTITY_TYPES.MULTI_SAMPLE]: [
        {
            tagName: 'app-multi-sample-table-widget',
            component: MultiSampleTableWidgetComponent,
            componentName: 'MultiSampleTableWidgetComponent',
            displayLabel: 'Multi-Sample Table Widget',
            inputs: ['data', MultiSampleTableWidgetAttr.ShowHeader, MultiSampleTableWidgetAttr.ExcludeAdjustedOel, 'excludedOelColumnKeys'],
            description: 'Multi-sample table widget showing sample information and agent concentrations across multiple samples',
            configureInputs: configureMultiSampleTableWidget,
            editorPropertySchema: multiSampleTableEditorPropertySchema,
            loopTemplate: multiSampleTableLoopTemplate,
            loopScopes: multiSampleLoopScopes
        }
    ],
    [ENTITY_TYPES.RISK_REGISTER]: [
        {
            tagName: 'app-risk-register-table-widget',
            component: RiskRegisterTableWidgetComponent,
            componentName: 'RiskRegisterTableWidgetComponent',
            displayLabel: 'Risk Register Matrix Widget',
            inputs: ['data'],
            description: 'Risk register matrix widget showing SEG x agent risk scores with colour-coded cells',
            configureInputs: configureRiskRegisterTableWidget,
            editorPropertySchema: riskRegisterTableEditorPropertySchema,
            loopTemplate: riskRegisterTableLoopTemplate,
            loopScopes: riskRegisterTableLoopScopes
        },
        {
            tagName: 'app-seg-profile-header-widget',
            component: SegProfileHeaderWidgetComponent,
            componentName: 'SegProfileHeaderWidgetComponent',
            displayLabel: 'SEG Profile Header Widget',
            inputs: ['profiles', SegProfileHeaderWidgetAttr.ShowDescription, SegProfileHeaderWidgetAttr.ShowContractors],
            description: 'SEG profile header showing name, description, code, workforce, shift type/length and average weekly hours',
            configureInputs: configureSegProfileHeaderWidget,
            editorPropertySchema: segProfileHeaderEditorPropertySchema,
            loopTemplate: segProfileHeaderLoopTemplate,
            loopScopes: segProfileHeaderLoopScopes
        },
        {
            tagName: 'app-seg-job-roles-widget',
            component: SegJobRolesWidgetComponent,
            componentName: 'SegJobRolesWidgetComponent',
            displayLabel: 'SEG Job Roles Widget',
            inputs: ['profiles'],
            description: 'SEG job roles table with per-role sample counts',
            configureInputs: configureSegJobRolesWidget,
            editorPropertySchema: segJobRolesEditorPropertySchema,
            loopTemplate: segJobRolesLoopTemplate,
            loopScopes: segJobRolesLoopScopes
        },
        {
            tagName: 'app-seg-agents-widget',
            component: SegAgentsWidgetComponent,
            componentName: 'SegAgentsWidgetComponent',
            displayLabel: 'SEG Per-Agent Table Widget',
            inputs: ['profiles', SegAgentsWidgetAttr.ShowResidual, SegAgentsWidgetAttr.ShowNotes],
            description: 'SEG per-agent table: exposure band, base/corrected OEL, correction, samples, ratings, control adequacy, compliance and status',
            configureInputs: configureSegAgentsWidget,
            editorPropertySchema: segAgentsEditorPropertySchema,
            loopTemplate: segAgentsLoopTemplate,
            loopScopes: segAgentsLoopScopes
        },
        {
            tagName: 'app-seg-controls-widget',
            component: SegControlsWidgetComponent,
            componentName: 'SegControlsWidgetComponent',
            displayLabel: 'SEG Controls Widget',
            inputs: ['profiles', SegControlsWidgetAttr.MaxControls],
            description: 'SEG controls table: code, name, type, bucket, owner, status, target/actual dates and link type',
            configureInputs: configureSegControlsWidget,
            editorPropertySchema: segControlsEditorPropertySchema,
            loopTemplate: segControlsLoopTemplate,
            loopScopes: segControlsLoopScopes
        },
        {
            tagName: 'app-seg-tasks-widget',
            component: SegTasksWidgetComponent,
            componentName: 'SegTasksWidgetComponent',
            displayLabel: 'SEG Tasks Widget',
            inputs: ['profiles', SegTasksWidgetAttr.ShowGroupHeadings],
            description: 'SEG task groups and their tasks',
            configureInputs: configureSegTasksWidget,
            editorPropertySchema: segTasksEditorPropertySchema,
            loopTemplate: segTasksLoopTemplate,
            loopScopes: segTasksLoopScopes
        }
    ]
};

WIDGET_REGISTRY[ENTITY_TYPES.CHAIN_OF_CUSTODY] = [
    {
        tagName: 'app-coc-sample-table-widget',
        component: CocSampleTableWidgetComponent,
        componentName: 'CocSampleTableWidgetComponent',
        displayLabel: 'Chain of Custody Sample Table Widget',
        inputs: ['html'],
        description: 'Chain of custody sample table with agents, sampling data and lab results',
        configureInputs: configureCocSampleTableWidget,
        editorPropertySchema: cocSampleTableEditorPropertySchema,
        loopTemplate: cocSampleTableLoopTemplate,
        loopScopes: cocSampleTableLoopScopes
    }
];

// EmployeeLetter reuses the single-sample widget set (identical to the Sample pool).
WIDGET_REGISTRY[ENTITY_TYPES.EMPLOYEE_LETTER] = WIDGET_REGISTRY[ENTITY_TYPES.SAMPLE];

export function getWidgetsForEntityType(entityType: string): WidgetRegistration[] {
    return WIDGET_REGISTRY[entityType] || [];
}

export function getAllWidgets(): WidgetRegistration[] {
    return Object.values(WIDGET_REGISTRY).flat();
}

export function getWidgetByTagName(tagName: string): WidgetRegistration | undefined {
    return getAllWidgets().find(widget => widget.tagName === tagName);
}

export function getWidgetByComponentName(componentName: string): WidgetRegistration | undefined {
    return getAllWidgets().find(widget => widget.componentName === componentName);
}

export function getWidgetDisplayLabel(tagName: string): string {
    return getWidgetByTagName(tagName)?.displayLabel ?? tagName;
}

export function getAllWidgetTags(): string[] {
    return getAllWidgets().map(w => w.tagName);
}
